<template>
  <app-layout>
    <template #header>
      <h1 class="text-xl font-semibold text-gray-900">S3 Migration Dashboard</h1>
    </template>

    <div class="py-12">
      <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
        <div class="bg-white overflow-hidden shadow-sm sm:rounded-lg">
          <div class="p-6 bg-white border-b border-gray-200">
            <div class="mb-8">
              <h2 class="text-lg font-semibold mb-4">Migration Progress</h2>
              <div class="w-full bg-gray-200 rounded-full h-4 mb-4">
                <div class="bg-blue-600 h-4 rounded-full" :style="{ width: `${stats.migrationProgress}%` }"></div>
              </div>
              <div class="text-right text-sm text-gray-500">{{ stats.migrationProgress.toFixed(2) }}% Complete</div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div class="bg-gray-50 p-4 rounded shadow">
                <h3 class="text-md font-medium mb-2">File Stats</h3>
                <div class="flex justify-between">
                  <span>Total Files:</span>
                  <span class="font-bold">{{ stats.totalFiles }}</span>
                </div>
                <div class="flex justify-between">
                  <span>Migrated:</span>
                  <span class="font-bold text-green-600">{{ stats.migratedFiles }}</span>
                </div>
                <div class="flex justify-between">
                  <span>Unmigrated:</span>
                  <span class="font-bold text-red-600">{{ stats.unmigratedFiles }}</span>
                </div>
              </div>

              <div class="bg-gray-50 p-4 rounded shadow">
                <h3 class="text-md font-medium mb-2">Penyedia Stats</h3>
                <div class="flex justify-between">
                  <span>Total Penyedia:</span>
                  <span class="font-bold">{{ stats.totalPenyedia }}</span>
                </div>
                <div class="flex justify-between">
                  <span>With Files:</span>
                  <span class="font-bold">{{ stats.penyediaWithFiles }}</span>
                </div>
              </div>

              <div class="bg-gray-50 p-4 rounded shadow">
                <h3 class="text-md font-medium mb-2">Quick Actions</h3>
                <div class="space-y-2">
                  <button @click="migrateBatch(10)" class="w-full bg-blue-500 hover:bg-blue-600 text-white py-1 px-3 rounded text-sm" :disabled="loading">
                    Migrate Batch (10)
                  </button>
                  <button @click="getStatus" class="w-full bg-gray-500 hover:bg-gray-600 text-white py-1 px-3 rounded text-sm" :disabled="loading">
                    Refresh Status
                  </button>
                </div>
              </div>
            </div>

            <div class="mb-8">
              <h2 class="text-lg font-semibold mb-4">Migration Operations</h2>
              
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div class="bg-gray-50 p-4 rounded shadow">
                  <h3 class="text-md font-medium mb-2">Migrate Batch</h3>
                  <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-1">Batch Size</label>
                    <input v-model="batchSize" type="number" min="1" max="100" class="border border-gray-300 rounded p-1 w-full" />
                  </div>
                  <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-1">Limit (optional)</label>
                    <input v-model="limit" type="number" min="1" class="border border-gray-300 rounded p-1 w-full" />
                  </div>
                  <button @click="migrateBatch()" class="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded" :disabled="loading">
                    Migrate Files
                  </button>
                </div>

                <div class="bg-gray-50 p-4 rounded shadow">
                  <h3 class="text-md font-medium mb-2">Migrate by Penyedia ID</h3>
                  <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-1">Penyedia ID</label>
                    <input v-model="penyediaId" type="number" min="1" class="border border-gray-300 rounded p-1 w-full" />
                  </div>
                  <button @click="migrateById()" class="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded" :disabled="loading">
                    Migrate Files for This Penyedia
                  </button>
                </div>
              </div>
            </div>

            <div v-if="output" class="mb-8">
              <h2 class="text-lg font-semibold mb-4">Command Output</h2>
              <div class="bg-gray-800 text-white p-4 rounded font-mono text-sm whitespace-pre-wrap">{{ output }}</div>
            </div>

            <div>
              <h2 class="text-lg font-semibold mb-4">Unmigrated Files</h2>
              <div v-if="loading" class="flex justify-center my-8">
                <div class="loader"></div>
              </div>
              <div v-else-if="unmigratedFiles.length === 0" class="text-center py-8 text-gray-500">
                No unmigrated files found.
              </div>
              <div v-else>
                <table class="min-w-full divide-y divide-gray-200">
                  <thead class="bg-gray-50">
                    <tr>
                      <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                      <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Penyedia</th>
                      <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Name</th>
                      <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Type</th>
                      <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-for="file in unmigratedFiles" :key="file.id">
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ file.id }}</td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ file.penyedia?.nama_perusahaan || `ID: ${file.penyedia_id}` }}</td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ file.nama_file }}</td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ file.jenis_file }}</td>
                      <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button @click="migrateById(file.penyedia_id)" class="text-blue-600 hover:text-blue-900 mr-2">Migrate</button>
                      </td>
                    </tr>
                  </tbody>
                </table>
                
                <div class="mt-4 flex justify-between">
                  <button 
                    @click="loadUnmigratedFiles(pagination.current_page - 1)" 
                    :disabled="!pagination.prev_page_url || loading" 
                    class="bg-gray-200 hover:bg-gray-300 py-1 px-3 rounded text-sm"
                  >
                    Previous
                  </button>
                  <span class="text-sm text-gray-600">Page {{ pagination.current_page }} of {{ pagination.last_page }}</span>
                  <button 
                    @click="loadUnmigratedFiles(pagination.current_page + 1)" 
                    :disabled="!pagination.next_page_url || loading" 
                    class="bg-gray-200 hover:bg-gray-300 py-1 px-3 rounded text-sm"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </app-layout>
</template>

<script>
import AppLayout from '@/Layouts/AppLayout.vue'
import { ref, onMounted } from 'vue'
import axios from 'axios'

export default {
  components: {
    AppLayout
  },
  props: {
    stats: Object
  },
  setup(props) {
    const loading = ref(false)
    const output = ref('')
    const unmigratedFiles = ref([])
    const pagination = ref({})
    const batchSize = ref(10)
    const limit = ref(null)
    const penyediaId = ref(null)

    const loadUnmigratedFiles = async (page = 1) => {
      loading.value = true
      try {
        const response = await axios.get('/s3migration/unmigrated', {
          params: {
            page: page,
            limit: 10
          }
        })
        
        unmigratedFiles.value = response.data.data
        pagination.value = {
          current_page: response.data.current_page,
          last_page: response.data.last_page,
          prev_page_url: response.data.prev_page_url,
          next_page_url: response.data.next_page_url
        }
      } catch (error) {
        console.error('Error loading unmigrated files:', error)
      } finally {
        loading.value = false
      }
    }

    const migrateBatch = async (size = null) => {
      loading.value = true
      output.value = 'Running migration batch...'
      
      try {
        const response = await axios.post('/s3migration/migrate-batch', {
          batch_size: size || batchSize.value,
          limit: limit.value
        })
        
        output.value = response.data.output
        // Update the stats with the latest data
        Object.assign(props.stats, response.data.stats)
        
        // Reload unmigrated files
        await loadUnmigratedFiles()
      } catch (error) {
        console.error('Error migrating batch:', error)
        output.value = 'Error: ' + (error.response?.data?.message || error.message)
      } finally {
        loading.value = false
      }
    }

    const migrateById = async (id = null) => {
      loading.value = true
      const targetId = id || penyediaId.value
      
      if (!targetId) {
        output.value = 'Error: Penyedia ID is required'
        loading.value = false
        return
      }
      
      output.value = `Running migration for Penyedia ID: ${targetId}...`
      
      try {
        const response = await axios.post('/s3migration/migrate-id', {
          id: targetId
        })
        
        output.value = response.data.output
        // Update the stats with the latest data
        Object.assign(props.stats, response.data.stats)
        
        // Reload unmigrated files
        await loadUnmigratedFiles()
      } catch (error) {
        console.error('Error migrating by ID:', error)
        output.value = 'Error: ' + (error.response?.data?.message || error.message)
      } finally {
        loading.value = false
      }
    }

    const getStatus = async () => {
      loading.value = true
      output.value = 'Getting migration status...'
      
      try {
        const response = await axios.get('/s3migration/status')
        
        output.value = response.data.output
        // Update the stats with the latest data
        Object.assign(props.stats, response.data.stats)
      } catch (error) {
        console.error('Error getting status:', error)
        output.value = 'Error: ' + (error.response?.data?.message || error.message)
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      loadUnmigratedFiles()
    })

    return {
      loading,
      output,
      unmigratedFiles,
      pagination,
      batchSize,
      limit,
      penyediaId,
      loadUnmigratedFiles,
      migrateBatch,
      migrateById,
      getStatus
    }
  }
}
</script>

<style scoped>
.loader {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: #3490dc;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
